import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { serviceAPI, bookingAPI, getAvailableTimes, getAvailabilityDates } from '../services/api';
import { DayPicker } from 'react-day-picker';
import { format, isToday, setHours, setMinutes, addMinutes, startOfMonth, endOfMonth, parseISO, addMonths } from 'date-fns';
import { ru as ruLocale, et as etLocale, fi as fiLocale } from 'date-fns/locale';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { useI18n } from '../i18n/i18nContext';
import { getTranslatedField } from '../utils/translationUtils';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNoIndex } from '../hooks/useNoIndex';
import { useBookingCartStore } from '../stores/bookingCartStore';
import { useServiceMetaStore } from '../stores/serviceMetaStore';
import { SuggestedServices } from '../components/SuggestedServices';
import { BookingCart } from '../components/BookingCart';
import { Toast } from '../components/Toast';
import LocalizedLink from '../components/LocalizedLink';
import { Service, TimeSlot } from '@shared/types';
import { TALLINN_TIMEZONE } from '@shared/constants';
import { pushToDataLayer, clearEcommerce, formatServiceItem } from '../utils/dataLayer';

interface LocationState {
  isRescheduling?: boolean;
  bookingId?: string;
  currentDate?: string;
  currentTime?: string;
  preselectedDate?: Date;
  preselectedTime?: string;
  isChanging?: boolean;
  cartItemId?: string;
}

const BookingCalendarPage: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const { t, language } = useI18n();
  useDocumentTitle(t.booking.selectDateTime);
  useNoIndex();
  const [searchParams] = useSearchParams();
  const userPackageId = searchParams.get('userPackageId');
  
  const locationState = location.state as LocationState | null;
  const isChanging = locationState?.isChanging || false;
  const cartItemIdToChange = locationState?.cartItemId;

  const [service, setService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    return locationState?.preselectedDate ? new Date(locationState.preselectedDate) : undefined;
  });
  const [month, setMonth] = useState<Date | undefined>(() => {
    return locationState?.preselectedDate ? new Date(locationState.preselectedDate) : new Date();
  });
  const [initialDateResolved, setInitialDateResolved] = useState<boolean>(() => {
    return !!locationState?.preselectedDate;
  });
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState<Date[]>([]);
  const [reschedulingSuccess, setReschedulingSuccess] = useState(false);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [lastOperationWasChange, setLastOperationWasChange] = useState(false);
   
  const { items, addBooking, updateBooking } = useBookingCartStore();
  const { setMeta } = useServiceMetaStore();

  const [isRescheduling] = useState(locationState?.isRescheduling || false);
  const lastFetchedDateRef = useRef<string | null>(null);
  const findFirstDateCalledRef = useRef(false);
  const hasTrackedViewItem = useRef(false);

  useEffect(() => {
    if (serviceId) {
      fetchService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  useEffect(() => {
    if (service && !hasTrackedViewItem.current) {
      hasTrackedViewItem.current = true;
      clearEcommerce();
      pushToDataLayer('view_item', {
        content: { category: 'booking_service' },
        ecommerce: {
          items: [formatServiceItem(service)]
        }
      });
    }
  }, [service]);

  useEffect(() => {
    if (serviceId && selectedDate && initialDateResolved) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      if (lastFetchedDateRef.current !== dateStr) {
        lastFetchedDateRef.current = dateStr;
        fetchAvailability(dateStr, selectedDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, selectedDate, initialDateResolved]);

  useEffect(() => {
    if (serviceId && month) {
      fetchMonthAvailability(month);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, month]);

  // This effect runs when the component is already mounted and the location state changes.
  // This is crucial for handling the "Change" action when the user is already on the page for the same service.
  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.isChanging && state.preselectedDate) {
      const newDate = new Date(state.preselectedDate);
      lastFetchedDateRef.current = null;
      setSelectedDate(newDate);
      setMonth(newDate);
    }
  }, [location.state]);

  const fetchService = async () => {
    if (!serviceId) return;
    
    try {
      const response = await serviceAPI.getById(serviceId);
      setService(response.data);

      // Store service metadata for use in the cart
      if (response.data.maxParticipants) {
        setMeta(serviceId, { maxParticipants: response.data.maxParticipants });
      }

      if (!locationState?.preselectedDate && !locationState?.isChanging) {
        await findFirstAvailableDate(serviceId);
      }
    } catch (err) {
      console.error(t.console.errors.fetchingService, err);
      navigate('/book');
    } finally {
      setLoading(false);
    }
  };

  const findFirstAvailableDate = async (id: string) => {
    if (findFirstDateCalledRef.current) return;
    findFirstDateCalledRef.current = true;
    
    try {
      const today = new Date();
      const endDate = addMonths(today, 4);
      const startStr = format(today, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      
      const response = await getAvailabilityDates(id, startStr, endStr);
      const availabilityMap = response.data.data;
      
      const availableDates = Object.entries(availabilityMap)
        .filter(([_, count]) => (count as number) > 0)
        .map(([dateStr]) => dateStr)
        .sort();
        
      if (availableDates.length > 0) {
        const firstAvailableDate = parseISO(availableDates[0]);
        setSelectedDate(firstAvailableDate);
        setMonth(firstAvailableDate);
        setLoadingSlots(true);
        setInitialDateResolved(true);
      } else {
        setSelectedDate(new Date());
        setInitialFetchDone(true);
        setInitialDateResolved(true);
      }
    } catch (err) {
      console.error('Failed to auto-select first available date', err);
    }
  };

  const fetchMonthAvailability = async (targetMonth: Date) => {
    if (!serviceId) return;

    try {
      const start = format(startOfMonth(targetMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(targetMonth), 'yyyy-MM-dd');

      const response = await getAvailabilityDates(serviceId, start, end);
      const availabilityMap = response.data.data;

      const disabledDates: Date[] = [];
      Object.entries(availabilityMap).forEach(([dateStr, count]) => {
        if (count === 0) {
          disabledDates.push(parseISO(dateStr));
        }
      });
      
      setUnavailableDates(disabledDates);
    } catch (err) {
      console.error('Failed to fetch month availability', err);
    }
  };

  const fetchAvailability = async (date: string, forDate: Date) => {
    if (!serviceId) return;
    
    setLoadingSlots(true);
    try {
      const response = await getAvailableTimes(serviceId, date, date);
      
      const slotsData = response.data.data || [];
      const slots = slotsData.map((slot: any, index: number) => ({
        id: slot.id || `${date}-${slot.time || slot}-${index}`,
        time: slot.time || slot,
        available: slot.available !== false,
        resourceId: slot.resourceId
      }));
      
      if (isToday(forDate)) {
        const now = new Date();
        const availableSlots = slots.filter((slot: TimeSlot) => {
          const [hour, minute] = slot.time.split(':').map(Number);
          const slotTime = setMinutes(setHours(forDate, hour), minute);
          return now < slotTime;
        });
        setTimeSlots(availableSlots);
      } else {
        setTimeSlots(slots);
      }

        if (isChanging && locationState?.preselectedTime) {
        const matchingSlot = slots.find((slot: TimeSlot) => slot.time === locationState.preselectedTime);
        if (matchingSlot) {
          setSelectedSlot(matchingSlot.id);
          // We no longer clear the state here, allowing the component to remember it's "changing".
        }
       }
    } catch (err) {
      console.error(t.console.errors.fetchingAvailability, err);
      // Fallback to empty slots on error
      setTimeSlots([]);
    } finally {
      setLoadingSlots(false);
      setInitialFetchDone(true);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      lastFetchedDateRef.current = null;
      setSelectedDate(date);
      setMonth(date);
    }
    setSelectedSlot(null);
  };

  const isSelectedSlotAlreadyBooked = () => {
    if (!selectedSlot || !selectedDate || !service) return false;
    
    const slot = timeSlots.find(s => s.id === selectedSlot);
    if (!slot) return false;
    
    const selectedDateString = format(selectedDate, 'yyyy-MM-dd');
    
    return items.some(item => {
      const itemDateString = formatInTimeZone(new Date(item.startTime), TALLINN_TIMEZONE, 'yyyy-MM-dd');
      const itemTimeString = formatInTimeZone(new Date(item.startTime), TALLINN_TIMEZONE, 'HH:mm');
      
      return item.serviceId === service.id && 
             itemDateString === selectedDateString && 
             itemTimeString === slot.time &&
             item.cartItemId !== cartItemIdToChange;
    });
  };

  const handleAddToCart = () => {
    if (selectedSlot && selectedDate && service) {
      const slot = timeSlots.find(s => s.id === selectedSlot);
      if (slot) {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const timeStr = slot.time;
        
        const dateTimeStr = `${dateStr} ${timeStr}`;
        
        const startDateTime = fromZonedTime(dateTimeStr, TALLINN_TIMEZONE);
        const endDateTime = addMinutes(startDateTime, service.duration);

        const bookingDetails = {
          serviceId: service.id,
          serviceName: getTranslatedField(service, 'name', language),
          serviceNameEn: service.name, // Store English name for GTM data consistency (default name field is English)
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          price: service.price || 0,
          duration: service.duration,
          quantity: 1,
          invitees: [],
        };

        if (isChanging && cartItemIdToChange) {
          updateBooking(cartItemIdToChange, bookingDetails);
          setLastOperationWasChange(true);
          navigate(location.pathname, { replace: true });
        } else {
          addBooking(bookingDetails);
          setLastOperationWasChange(false);
        }

        clearEcommerce();
        // Standardized format: YYYY-MM-DD HH:mm for GA4 data consistency
        const variant = `${dateStr} ${timeStr}`;
        pushToDataLayer('add_to_cart', {
          content: { category: 'booking_service' },
          ecommerce: {
            items: [formatServiceItem(service, variant)]
          }
        });

        setShowCartNotification(true);
        setSelectedSlot(null);
      }
    }
  };

  const handleContinue = async () => {
    if (selectedSlot && selectedDate) {
      const slot = timeSlots.find(s => s.id === selectedSlot);
      if (slot) {
        // If rescheduling, directly call reschedule API
        if (isRescheduling && locationState?.bookingId) {
          try {
            setLoading(true);
            await bookingAPI.reschedule(locationState.bookingId, {
              date: format(selectedDate, 'yyyy-MM-dd'),
              time: slot.time,
              language
            });
            setReschedulingSuccess(true);
            // Navigate back to account page with success message
            setTimeout(() => {
              navigate('/account', {
                state: { message: t.booking.reschedulingSuccess }
              });
            }, 1500);
          } catch (error: any) {
            console.error(t.console.errors.reschedulingBooking, error);
            // Check if it's a 24-hour policy violation
            if (error.response?.data?.error?.includes('24 hours')) {
              alert(t.account.reschedulingPolicy24Hours || 'Bookings cannot be rescheduled within 24 hours of the appointment time.');
            } else {
              alert(t.booking.reschedulingError);
            }
            setLoading(false);
          }
        } else {
          // Normal booking flow
          navigate(`/book/${serviceId}/confirm${userPackageId ? '?userPackageId=' + userPackageId : ''}`, {
            state: {
              service,
              date: format(selectedDate, 'yyyy-MM-dd'),
              time: slot.time,
              slotId: selectedSlot,
              resourceId: slot.resourceId
            }
          });
        }
      }
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">{t.common.loading}</div>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  return (
    <div className="py-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {showCartNotification && (
        <Toast
          message={lastOperationWasChange ? (t.cart.bookingChanged || 'Booking has been changed') : t.cart.addedToCart}
          type="success"
          onClose={() => setShowCartNotification(false)}
        />
      )}
      {reschedulingSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-800">{t.booking.reschedulingSuccess}</p>
        </div>
      )}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isRescheduling ? (t.booking.rescheduleTitle || 'Reschedule Booking') : t.booking.title}
        </h1>
        <p className="text-lg text-gray-600">
          {isRescheduling ? (t.booking.rescheduleSubtitle || 'Choose a new date and time for your booking') : t.booking.step2ChooseDateTime}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* Service Description, Calendar and Time Selection */}
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-8">
            {/* Service Description */}
            <div className="lg:col-span-3 lg:row-span-3">
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                {service.image && (
                  <img
                    src={service.image}
                    alt={getTranslatedField(service, 'name', language)}
                    className="w-full h-48 object-cover rounded-md mb-4"
                  />
                )}
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {getTranslatedField(service, 'name', language)}
                  </h2>
                  <LocalizedLink
                    to="/pricing"
                    className="ml-3 px-3 py-1 text-sm font-medium text-sky-600 bg-sky-50 rounded-md hover:bg-sky-100 transition-colors whitespace-nowrap"
                  >
                    {t.nav.pricing}
                  </LocalizedLink>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    {service.duration} {t.booking.minutes}
                  </p>
                {isRescheduling && locationState?.currentDate && locationState?.currentTime && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-md">
                      <p className="text-sm text-gray-600">
                        {t.booking.currentBooking || 'Current booking'}: {formatInTimeZone(new Date(locationState.currentDate), TALLINN_TIMEZONE, 'EEE, MMM d', { locale: language === 'ru' ? ruLocale : language === 'et' ? etLocale : language === 'fi' ? fiLocale : undefined })} at {locationState.currentTime}
                      </p>
                    </div>
                  )}
                </div>
                {getTranslatedField(service, 'description_long', language) && (
                  <div className="border-t border-gray-200 pt-4">
                    <div 
                      className="text-gray-700 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: getTranslatedField(service, 'description_long', language) || '' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Date Selection */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4">{t.booking.selectDate}</h3>
              <div className="bg-white p-4 rounded-md shadow overflow-x-auto flex justify-center md:block">
                <DayPicker
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  month={month}
                  onMonthChange={setMonth}
                  disabled={[
                    { before: new Date() },
                    ...unavailableDates
                  ]}
                  locale={language === 'ru' ? ruLocale : language === 'et' ? etLocale : language === 'fi' ? fiLocale : undefined}
                  className="mx-auto"
                  styles={{
                    caption: { color: '#1d4ed8' },
                    head: { color: '#1d4ed8' },
                  }}
                />
              </div>
            </div>

            {/* Time Slots */}
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4">{t.booking.selectTime}</h3>
              {loadingSlots || !initialDateResolved || !initialFetchDone ? (
                <div className="py-8" />
              ) : timeSlots.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {t.booking.noAvailableTimesForDate}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-96 overflow-y-auto">
                  {timeSlots.map((slot) => {
                    const isAlreadyBooked = selectedDate && items.some(item => {
                      const itemDate = formatInTimeZone(new Date(item.startTime), TALLINN_TIMEZONE, 'yyyy-MM-dd');
                      const itemTime = formatInTimeZone(new Date(item.startTime), TALLINN_TIMEZONE, 'HH:mm');
                      const currentDate = formatInTimeZone(selectedDate, TALLINN_TIMEZONE, 'yyyy-MM-dd');
                      return item.serviceId === serviceId && itemDate === currentDate && itemTime === slot.time;
                    });
                    
                    return (
                      <button
                        key={slot.id}
                        disabled={!slot.available}
                        onClick={() => setSelectedSlot(slot.id)}
                        className={`py-2 px-2 rounded-md text-xs sm:text-sm font-medium transition-colors touch-manipulation ${
                          !slot.available
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : isAlreadyBooked
                            ? 'bg-purple-100 border-2 border-purple-500 text-purple-700'
                            : selectedSlot === slot.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {slot.time}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Action Buttons - Below date/time selection, spanning 4 columns */}
            <div className="lg:col-span-4 lg:col-start-4 flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
              <button
                onClick={() => isRescheduling ? navigate('/account') : navigate('/book')}
                className="w-full sm:w-auto px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
              >
                {isRescheduling ? t.common.cancel : t.cart.addAnotherService}
              </button>
              {!isRescheduling && (
                <button
                  onClick={handleAddToCart}
                  disabled={!selectedSlot || isSelectedSlotAlreadyBooked()}
                  className={`w-full sm:w-auto px-6 py-2 rounded-md font-medium ${
                    selectedSlot && !isSelectedSlotAlreadyBooked()
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isChanging ? (t.cart.confirmChange || 'Confirm Change') : (t.cart.addToCart || 'Book Time')}
                </button>
              )}
              {isRescheduling && (
                <button
                  onClick={handleContinue}
                  disabled={!selectedSlot || loading}
                  className={`w-full sm:w-auto px-6 py-2 rounded-md font-medium ${
                    selectedSlot && !loading
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {loading ? t.common.loading : (t.booking.confirmReschedule || 'Confirm Reschedule')}
                </button>
              )}
            </div>
            
            {/* Booking Cart - Below action buttons, spanning 4 columns */}
            {!isRescheduling && items.length > 0 && (
              <div className="lg:col-span-4 lg:col-start-4">
                <BookingCart />
              </div>
            )}
          </div>
          
          <SuggestedServices />
        </div>
      </div>
    </div>
  );
};

export default BookingCalendarPage;